import ky from 'ky';
import config from '@/lib/config';
import type { CreateSessionRequest, SecretarySession, UploadChunkResult } from './types';

const BASE_URL = config.SECRETARY_API_URL;

/** Same Supabase-token resolution as notes/tasks (unia-auth-token → token → cookie). */
export function getSecretaryToken(): string | null {
  try {
    const ls = localStorage.getItem('unia-auth-token');
    if (ls) {
      try {
        const session = JSON.parse(ls);
        if (session?.access_token) return session.access_token;
      } catch {
        if (ls.startsWith('eyJ')) return ls;
      }
    }
    const legacy = localStorage.getItem('token');
    if (legacy) return legacy;
    const match = document.cookie.match(/(?:^|;\s*)unia-auth-token=([^;]+)/);
    if (!match) return null;
    const raw = decodeURIComponent(match[1]);
    if (raw.startsWith('eyJ')) return raw;
    const session = JSON.parse(raw);
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

const client = ky.create({
  prefixUrl: BASE_URL,
  timeout: 60000,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getSecretaryToken();
        if (token) request.headers.set('Authorization', `Bearer ${token}`);
      },
    ],
  },
});

export interface SecretaryProject {
  id: string;
  name: string;
}

/** Организация, в которую уйдёт запись: та, что записана в пропуске. */
function tokenTenantId(): string | null {
  const token = getSecretaryToken();
  if (!token) return null;
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(decodeURIComponent(escape(atob(part))));
    return claims?.app_metadata?.tenant_id ?? claims?.tenant_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Проекты, в которые можно положить запись: рабочие проекты человека в ТОЙ
 * организации, куда уйдёт запись (проект чужой организации Секретарь не
 * примет). Без проекта протокол и итоги встречи висят «на согласовании».
 */
export async function listProjects(): Promise<SecretaryProject[]> {
  const tenant = tokenTenantId();
  const token = getSecretaryToken();
  const rows = await ky
    .get(`${config.TASK_API_URL}/api/projects`, {
      timeout: 20000,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
    .json<Array<{ id: string; name: string; orgId?: string | null }>>();
  return rows
    .filter((p) => !tenant || !p.orgId || p.orgId === tenant)
    .map((p) => ({ id: p.id, name: p.name }));
}

const ПРОЕКТ_КЛЮЧ = 'secretary-last-project';

/** Последний выбранный проект — чтобы не выбирать каждый раз. */
export function rememberedProject(): string | null {
  try {
    return localStorage.getItem(`${ПРОЕКТ_КЛЮЧ}:${tokenTenantId() ?? ''}`);
  } catch {
    return null;
  }
}

export function rememberProject(id: string): void {
  try {
    localStorage.setItem(`${ПРОЕКТ_КЛЮЧ}:${tokenTenantId() ?? ''}`, id);
  } catch {
    /* браузер не даёт хранить — выберут ещё раз */
  }
}

export async function createSession(req: CreateSessionRequest): Promise<SecretarySession> {
  return client.post('api/secretary/sessions', { json: req }).json<SecretarySession>();
}

export async function uploadChunk(
  sessionId: string,
  seq: number,
  offsetMs: number,
  blob: Blob,
  filename: string,
): Promise<UploadChunkResult> {
  const form = new FormData();
  form.append('seq', String(seq));
  form.append('offset_ms', String(offsetMs));
  form.append('audio', blob, filename);
  return client
    .post(`api/secretary/sessions/${sessionId}/audio-chunks`, { body: form })
    .json<UploadChunkResult>();
}

/**
 * Принести ГОТОВУЮ запись встречи одним файлом.
 *
 * Записать разговор прямо здесь можно было и раньше. А вот всё, что уже
 * лежит в телефоне — диктофон Android или iOS, пересланное голосовое, запись
 * созвона — попасть к секретарю не могло вовсе, хотя именно такие записи у
 * людей и есть.
 *
 * Загрузка идёт через XMLHttpRequest, а не ky: на часовой записи это десятки
 * мегабайт, и человеку нужно видеть, сколько уже ушло.
 */
export async function uploadRecording(
  file: File,
  fields: { title?: string; meeting_type?: string; participants?: string; project_id?: string } = {},
  onProgress?: (percent: number) => void,
): Promise<SecretarySession> {
  const form = new FormData();
  form.append('audio', file, file.name);
  if (fields.title) form.append('title', fields.title);
  form.append('meeting_type', fields.meeting_type || 'other');
  if (fields.participants) form.append('participants', fields.participants);
  if (fields.project_id) form.append('project_id', fields.project_id);

  return new Promise<SecretarySession>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}/api/secretary/sessions/upload`);
    const token = getSecretaryToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as SecretarySession);
        } catch {
          reject(new Error('Непонятный ответ сервера'));
        }
      } else {
        let detail = `Код ${xhr.status}`;
        try {
          const j = JSON.parse(xhr.responseText);
          if (j?.detail) detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
        } catch {
          /* тело не json */
        }
        reject(new Error(detail));
      }
    };
    xhr.onerror = () => reject(new Error('Связь с сервером оборвалась'));
    xhr.send(form);
  });
}

export async function stopSession(sessionId: string): Promise<{ ok: boolean; status: string }> {
  return client.post(`api/secretary/sessions/${sessionId}/stop`).json();
}

export async function getSession(sessionId: string): Promise<SecretarySession> {
  return client.get(`api/secretary/sessions/${sessionId}`).json<SecretarySession>();
}

/**
 * Open the SSE status stream for a session. EventSource can't set headers, and putting the
 * user's token in the URL leaks it into proxy logs and history — so we first fetch a
 * one-minute ticket bound to this session, then open the stream with it.
 * Falls back to polling getSession if EventSource is unavailable.
 */
export function openStatusStream(
  sessionId: string,
  onStatus: (s: { status: string; error?: string | null }) => void,
): () => void {
  let closed = false;

  try {
    if (typeof EventSource === 'undefined') throw new Error('no EventSource');
    let es: EventSource | null = null;
    client
      .post(`api/secretary/sessions/${sessionId}/events-ticket`)
      .json<{ ticket: string }>()
      .then(({ ticket }) => {
        if (closed) return;
        es = new EventSource(
          `${BASE_URL}/api/secretary/sessions/${sessionId}/events?ticket=${encodeURIComponent(ticket)}`,
          { withCredentials: false },
        );
        es.addEventListener('status', (ev) => {
          try {
            onStatus(JSON.parse((ev as MessageEvent).data));
          } catch {
            /* ignore malformed frame */
          }
        });
        es.onerror = () => {
          /* keep the connection; browser auto-reconnects */
        };
      })
      .catch(() => {
        /* ticket failed — the caller also polls getSession */
      });
    return () => {
      closed = true;
      es?.close();
    };
  } catch {
    // Polling fallback.
    const timer = setInterval(async () => {
      if (closed) return;
      try {
        const s = await getSession(sessionId);
        onStatus({ status: s.status });
        if (s.status === 'done' || s.status === 'failed') clearInterval(timer);
      } catch {
        /* retry next tick */
      }
    }, 3000);
    return () => {
      closed = true;
      clearInterval(timer);
    };
  }
}

export const secretaryApi = {
  createSession,
  listProjects,
  rememberedProject,
  rememberProject,
  uploadChunk,
  uploadRecording,
  stopSession,
  getSession,
  openStatusStream,
};
